import { Checkbox } from "@fluentui/react"
import axios from "axios"
import moment from "moment"
import { useEffect, useRef, useState, type KeyboardEvent } from "react"
import { Button, Modal } from "react-bootstrap"
import { Prompt, Redirect } from "react-router"
import type ContractDetails from "src/pages/settings/css_Settings/contract_maintenance/interfaces/ContractDetails"
import AxiosConfig from "src/utilities/AxiosConfig"
import { qsStringify } from "src/utilities/helper"
import snackBar from "src/utilities/snackBar/SnackBar"
import logger from "../../../utilities/logging"
import TextPopUp from "./ContractMaintenanceTextExtend"

interface BaseContactInfoType {
	ContractID: number
	CustomerID: number
	CustomerName: string
	Contract_Description: string
	VP_InCharge: string
	State: string
	PaymentTerms: number
	Discount: string
	StartDate: string
	EndDate: string
	IsAutoRenew: boolean
	CPI: boolean
	CPI_Character: string
	CPIAmount: number
	CPIEffectiveDate: string
	FuelSurcharge: boolean
	SurCharge_Character: string
	FuelSurchargeAmount: number
	FuelEffectiveDate: string
	FuelEndDate: string
	PriceIncrease: number
	Comments: string
	IsActive: boolean
	IsDistributed: boolean
	DistributionDate: string
	IsTermableClause: string
	TermNotes: string
	IsLateFee: boolean
	LateFeePercentage: number
	LateFeeTerm: number
	LimitLiability: boolean
	LiabilityComments: string
}
interface customerDataType {
	CustomerName: string
	CustomerID: number
}
interface ContractMaintenanceEditableCellInterface {
	data: BaseContactInfoType
	customerData?: customerDataType[]
	index: number
	edit: boolean
	renew: () => void
	id: number
}
//r-18-lint-cn
//eslint-disable-next-line
interface EventDictionary<TValue> {
	[id: string]: TValue
}
function ContractMaintenanceEditableCell(
	props: ContractMaintenanceEditableCellInterface,
) {
	const index = props.index
	// Spread to avoid sharing a reference with props.data — mutation in setEditData would otherwise corrupt the original
	const [data, setData] = useState<BaseContactInfoType>({ ...props.data })
	const [isEditing, setIsEditing] = useState<boolean>(props.edit)
	const [customerData, setCustomerData] = useState<customerDataType[]>()
	const [edited, setEdited] = useState<string[]>([])
	const invalidWarningStyle = {
		backgroundColor: "ivory",
		border: "none",
		outline: "2px solid red",
		borderRadius: "5px",
	}
	const nullPlacment = ""
	const required = [
		"CustomerName",
		"CustomerID",
		"VP_InCharge",
		"State",
		"PaymentTerms",
		"StartDate",
		"EndDate",
		"IsTermableClause",
	]
	const PaymentTermsRef = useRef<HTMLInputElement>(null)
	const VPRef = useRef<HTMLInputElement>(null)
	const StateRef = useRef<HTMLInputElement>(null)
	const [invalidInputs, setInvalidInputs] = useState<string[]>([])
	const [error, setError] = useState(false)
	const [deleteCon, setDeleteCon] = useState(false)
	const [cancelCon, setCancelCon] = useState(false)
	const [saveCon, setSaveCon] = useState(false)
	const setEditData = (key: keyof BaseContactInfoType, edit: string) => {
		const alter = { ...data }
		const e = edited
		if (key === "CustomerName") {
			alter[key] = edit
			alter.CustomerID =
				customerData?.find((x) => x.CustomerName === edit)?.CustomerID ?? -99
			if (editCompare(edit, props.data[key])) {
				const r = e.filter((x) => x !== key)
				setEdited(r)
			} else if (!e.includes(key) && edit !== props.data[key]) {
				e.push(key)
				setEdited(e)
			}
		} else if (
			key === "PaymentTerms" ||
			key === "CPIAmount" ||
			key === "FuelSurchargeAmount" ||
			key === "PriceIncrease" ||
			key === "LateFeePercentage" ||
			key === "LateFeeTerm" ||
			key === "CustomerID" ||
			key === "ContractID"
		) {
			alter[key] = Number(edit)
			if (editCompare(edit, props.data[key])) {
				const r = e.filter((x) => x !== key)
				setEdited(r)
			} else if (!e.includes(key) && Number(edit) !== props.data[key]) {
				e.push(key)
				setEdited(e)
			}
		} else if (
			key === "IsAutoRenew" ||
			key === "CPI" ||
			key === "FuelSurcharge" ||
			key === "IsActive" ||
			key === "IsDistributed" ||
			key === "IsLateFee" ||
			key === "LimitLiability"
		) {
			alter[key] = !alter[key]
			if (editCompare(edit, props.data[key]?.toString())) {
				const r = e.filter((x) => x !== key)
				setEdited(r)
			} else if (!e.includes(key) && edit !== props.data[key]?.toString()) {
				e.push(key)
				setEdited(e)
			}
		} else if (key === "StartDate") {
			alter[key] = edit
			if (editCompare(edit, props.data[key])) {
				const r = e.filter((x) => x !== key)
				setEdited(r)
			} else if (!e.includes(key) && edit !== props.data[key]) {
				e.push(key)
				setEdited(e)
			}
		} else {
			alter[key] = edit
			if (editCompare(edit, props.data[key])) {
				const r = e.filter((x) => x !== key)
				setEdited(r)
			} else if (!e.includes(key) && edit !== props.data[key]) {
				e.push(key)
				setEdited(e)
			}
		}
		setData({ ...alter })
	}

	const editCompare = (a: string, b: string | number) => {
		if (a === "" && b === null) {
			return true
		} else {
			return a === b
		}
	}

	const uploadData = () => {
		let keys = Object.keys(data as BaseContactInfoType)
		keys = keys.filter(
			(x) => data[x as keyof BaseContactInfoType]?.toString().trim() !== "",
		)
		const forgotten = required.filter(
			(x) => keys.find((y) => y === x) === undefined,
		)
		if (
			!data.StartDate ||
			Number.isNaN(moment(data.EndDate).valueOf()) ||
			data.StartDate > data.EndDate
		) {
			alert("Invalid Start Date/End Date pair")
			return null
		}
		if (!Array.isArray(forgotten) || !forgotten.length) {
			axios
				.post(
					`${process.env.REACT_APP_API_URL}/api/billing/UpdateBaseContractInfo`,
					qsStringify({
						...data,
					}),
					AxiosConfig.configValue(),
				)
				.then((res) => {
					if (res.data.error) {
						setIsEditing(false)
						throw Error(res.data.error.message)
					}
					setEdited([])
					setIsEditing(false) // must be explicit — no longer a side effect of the useEffect data reset
				})
				.then(() => {
					if (data.CustomerID) {
						const rb: ContractDetails = {
							CustomerID: data.CustomerID,
							//@ts-expect-error
							BusinessHoursStart: data.BusinessHoursStart,
							//@ts-expect-error
							BusinessHoursEnd: data.BusinessHoursEnd,
						}
						//@ts-expect-error
						if (!data.MissingContractDetails) {
							axios
								.post(
									`${process.env.REACT_APP_API_URL}/api/billing/updateContractDetails`,
									rb,
									AxiosConfig.configValue("min"),
								)
								.catch((err) =>
									logger.Error({
										EventMessage: `ContractMaintenance -> updateContractDetails -> Error : ${JSON.stringify(err)}`,
										EventCategory: "httprequest",
									}),
								)
						} else {
							axios
								.post(
									`${process.env.REACT_APP_API_URL}/api/billing/addContractDetails`,
									rb,
									AxiosConfig.configValue("min"),
								)
								.catch((err) =>
									logger.Error({
										EventMessage: `ContractMaintenance -> addContractDetails -> Error : ${JSON.stringify(err)}`,
										EventCategory: "httprequest",
									}),
								)
						}
					} else {
						snackBar({
							type: "info",
							text: "There is no CustomerID for this contract, business hours cannot be saved",
							timeout: 5000,
						})
					}
				})
				.finally(() => props.renew())
				.catch((error) => {
					alert(
						`There was an error Updating Contract Info(${error}). Reloading page...`,
					)
					//console.log(error);
					logger.Error({
						EventMessage: `ContractMaintenance -> UpdateContractInfo -> Error : ${JSON.stringify(error)}`,
						EventCategory: "httprequest",
					})
					AxiosConfig.handleError(error)
					setError(true)
					return null
				})
		} else {
			alert(`Invalid inputs in columns ${forgotten}`)
			if (forgotten.find((x) => x === "VP_InCharge")) {
				VPRef.current?.focus()
			} else if (forgotten.find((x) => x === "State")) {
				StateRef.current?.focus()
			} else if (forgotten.find((x) => x === "PaymentTerms")) {
				PaymentTermsRef.current?.focus()
			}
		}
		setInvalidInputs(forgotten)
	}

	const deleteData = (id: number) => {
		axios
			.get(
				`${process.env.REACT_APP_API_URL}/api/billing/DeleteBaseContractInfo/${Date.now()}/${props.id}/${id}`,
				AxiosConfig.configValue(),
			)
			.then((res) => {
				if (res.data.error) {
					throw Error(res.data.error.message)
				}
			})
			.finally(() => props.renew())
			.catch((error) => {
				alert(
					`There was an error Deleting Contract Info (${error}). Reloading page...`,
				)
				//console.log(error);
				logger.Error({
					EventMessage: `ContractMaintenance -> DeleteContractInfo -> Error : ${JSON.stringify(error)}`,
					EventCategory: "httprequest",
				})
				AxiosConfig.handleError(error)
				setError(true)
				return null
			})
	}

	const editBusinessHours = (
		event: { target: { value: string } },
		key: string,
	) => {
		const input = event.target.value
		const newData = { ...data }
		//@ts-expect-error
		newData[key] = input
		setData(newData)
	}

	// Only sync server data when not editing — prevents a sibling row save (which triggers renew → new props.data refs) from wiping in progress edits
	useEffect(() => {
		if (!isEditing) {
			setData({ ...props.data })
		}
	}, [props.data])

	// When the parent closes edit mode externally, also reset data so the read-only row doesn't briefly show stale edits
	useEffect(() => {
		setIsEditing(props.edit)
		if (!props.edit) {
			setData({ ...props.data })
		}
	}, [props.edit])

	useEffect(() => {
		if (props.customerData) {
			setCustomerData([...props.customerData])
		}
	}, [props.customerData])

	return (
		<>
			{isEditing ? (
				<tr key={index}>
					<td className="sticky-table-column-left">
						{data.ContractID ?? nullPlacment}
					</td>
					<td>{data.CustomerID ?? nullPlacment}</td>
					<td>
						<div className="editableSelect">
							{/* Match by ID so the correct option is highlighted even if names differ between the row data and customerData */}
							<select
								value={String(data.CustomerID)}
								onChange={(e) => {
									const customer = customerData?.find(
										(x) => String(x.CustomerID) === e.target.value,
									)
									if (customer)
										setEditData("CustomerName", customer.CustomerName)
								}}
							>
								{/* If CustomerID has no match in customerData (null ID or test/deleted customer),
                                        inject a disabled placeholder so the dropdown doesn't default to the first item */}
								{!customerData?.some(
									(x) => String(x.CustomerID) === String(data.CustomerID),
								) && (
									<option value={String(data.CustomerID)} disabled>
										{data.CustomerName ?? "Unknown"}
									</option>
								)}
								{customerData?.map((x) => (
									<option key={x.CustomerID} value={String(x.CustomerID)}>
										{x.CustomerName}
									</option>
								))}
							</select>
							{edited.includes("CustomerName") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editPop">
							<TextPopUp
								text={data.Contract_Description ?? nullPlacment}
								onChange={(e: string) => {
									setEditData("Contract_Description", e)
								}}
								warning={edited.includes("Contract_Description")}
								disabled={false}
								maxLength={150}
							/>
						</div>
					</td>
					<td>
						<div className="editMedium">
							<input
								type="text"
								value={data.VP_InCharge ?? nullPlacment}
								onChange={(e) => setEditData("VP_InCharge", e.target.value)}
								style={
									invalidInputs.find((x) => x === "VP_InCharge")
										? invalidWarningStyle
										: undefined
								}
								ref={VPRef}
								maxLength={50}
							/>
							{edited.includes("VP_InCharge") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editSmall">
							<input
								type="text"
								value={data.State ?? nullPlacment}
								onChange={(e) => setEditData("State", e.target.value)}
								style={
									invalidInputs.find((x) => x === "State")
										? invalidWarningStyle
										: undefined
								}
								ref={StateRef}
								maxLength={3}
							/>
							{edited.includes("State") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editSmall">
							<input
								type="number"
								value={data.PaymentTerms ?? ""}
								onChange={(e) => setEditData("PaymentTerms", e.target.value)}
								style={
									invalidInputs.find((x) => x === "PaymentTerms")
										? invalidWarningStyle
										: undefined
								}
								ref={PaymentTermsRef}
								onKeyDown={(event: KeyboardEvent<HTMLDivElement>) =>
									["e", "E", "+", "-"].includes(event.key) &&
									event.preventDefault()
								}
							/>
							{edited.includes("PaymentTerms") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editPop">
							<TextPopUp
								text={data.Discount}
								onChange={(e: string) => {
									setEditData("Discount", e)
								}}
								warning={edited.includes("Discount")}
								disabled={false}
								maxLength={20}
							/>
						</div>
					</td>
					<td>
						<div className="editDate">
							<input
								type="date"
								onChange={(e) => {
									setEditData("StartDate", moment(e.target.value).format())
								}}
								value={data.StartDate ? moment(data.StartDate).format("YYYY-MM-DD") : ""}
							/>
							{edited.includes("StartDate") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editDate">
							<input
								type="date"
								onChange={(e) => {
									setEditData("EndDate", moment(e.target.value).format())
								}}
								value={data.EndDate ? moment(data.EndDate).format("YYYY-MM-DD") : ""}
							/>
							{edited.includes("EndDate") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editCheck">
							<Checkbox
								checked={data.IsAutoRenew ?? nullPlacment}
								onChange={(_e) =>
									setEditData("IsAutoRenew", (!data.IsAutoRenew).toString())
								}
							/>
							{edited.includes("IsAutoRenew") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editCheck">
							<Checkbox
								checked={data.CPI ?? nullPlacment}
								onChange={(_e) => setEditData("CPI", (!data.CPI).toString())}
							/>
							{edited.includes("CPI") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editSmall">
							<input
								type="text"
								value={data.CPI_Character ?? nullPlacment}
								onChange={(e) => setEditData("CPI_Character", e.target.value)}
								maxLength={1}
							></input>
							{edited.includes("CPI_Character") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editSmall">
							<input
								type="number"
								value={data.CPIAmount ?? ""}
								onChange={(e) => setEditData("CPIAmount", e.target.value)}
								onKeyDown={(event: KeyboardEvent<HTMLDivElement>) =>
									["e", "E", "+", "-"].includes(event.key) &&
									event.preventDefault()
								}
							/>
							{edited.includes("CPIAmount") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editDate">
							<input
								type="date"
								onChange={(e) => {
									setEditData(
										"CPIEffectiveDate",
										moment(e.target.value).format(),
									)
								}}
								value={data.CPIEffectiveDate ? moment(data.CPIEffectiveDate).format("YYYY-MM-DD") : ""}
							/>
							{edited.includes("CPIEffectiveDate") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editCheck">
							<Checkbox
								checked={data.FuelSurcharge ?? nullPlacment}
								onChange={(_e) =>
									setEditData("FuelSurcharge", (!data.FuelSurcharge).toString())
								}
							/>
							{edited.includes("FuelSurcharge") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editSmall">
							<input
								type="text"
								value={data.SurCharge_Character ?? nullPlacment}
								onChange={(e) =>
									setEditData("SurCharge_Character", e.target.value)
								}
								maxLength={1}
							></input>
							{edited.includes("SurCharge_Character") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editSmall">
							<input
								type="number"
								value={data.FuelSurchargeAmount ?? ""}
								onChange={(e) =>
									setEditData("FuelSurchargeAmount", e.target.value)
								}
								onKeyDown={(event: KeyboardEvent<HTMLDivElement>) =>
									["e", "E", "+", "-"].includes(event.key) &&
									event.preventDefault()
								}
							/>
							{edited.includes("FuelSurchargeAmount") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editDate">
							<input
								type="date"
								onChange={(e) => {
									setEditData(
										"FuelEffectiveDate",
										moment(e.target.value).format(),
									)
								}}
								value={data.FuelEffectiveDate ? moment(data.FuelEffectiveDate).format("YYYY-MM-DD") : ""}
							/>
							{edited.includes("FuelEffectiveDate") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editDate">
							<input
								type="date"
								onChange={(e) => {
									setEditData("FuelEndDate", moment(e.target.value).format())
								}}
								value={data.FuelEndDate ? moment(data.FuelEndDate).format("YYYY-MM-DD") : ""}
							/>
							{edited.includes("FuelEndDate") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editSmall">
							<input
								type="number"
								value={data.PriceIncrease ?? ""}
								onChange={(e) => setEditData("PriceIncrease", e.target.value)}
								onKeyDown={(event: KeyboardEvent<HTMLDivElement>) =>
									["e", "E", "+", "-"].includes(event.key) &&
									event.preventDefault()
								}
							/>
							{edited.includes("PriceIncrease") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editPop">
							<TextPopUp
								text={data.Comments}
								onChange={(e: string) => {
									setEditData("Comments", e)
								}}
								warning={edited.includes("Comments")}
								disabled={false}
								maxLength={3500}
							/>
						</div>
					</td>
					<td>
						<div className="editCheck">
							<Checkbox
								checked={data.IsActive ?? nullPlacment}
								onChange={(_e) =>
									setEditData("IsActive", (!data.IsActive).toString())
								}
							/>
							{edited.includes("IsActive") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editCheck">
							<Checkbox
								checked={data.IsDistributed ?? nullPlacment}
								onChange={(_e) =>
									setEditData("IsDistributed", (!data.IsDistributed).toString())
								}
							/>
							{edited.includes("IsDistributed") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editDate">
							<input
								type="date"
								onChange={(e) => {
									setEditData(
										"DistributionDate",
										moment(e.target.value).format(),
									)
								}}
								value={data.DistributionDate ? moment(data.DistributionDate).format("YYYY-MM-DD") : ""}
							/>
							{edited.includes("DistributionDate") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editSmall">
							<input
								type="text"
								value={data.IsTermableClause ?? nullPlacment}
								onChange={(e) =>
									setEditData("IsTermableClause", e.target.value)
								}
								maxLength={10}
							></input>
							{edited.includes("IsTermableClause") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editPop">
							<TextPopUp
								text={data.TermNotes}
								onChange={(e: string) => {
									setEditData("TermNotes", e)
								}}
								warning={edited.includes("TermNotes")}
								disabled={false}
								maxLength={5000}
							/>
						</div>
					</td>
					<td>
						<div className="editCheck">
							<Checkbox
								checked={data.IsLateFee ?? nullPlacment}
								onChange={(_e) =>
									setEditData("IsLateFee", (!data.IsLateFee).toString())
								}
							/>
							{edited.includes("IsLateFee") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editSmall">
							<input
								type="number"
								value={data.LateFeePercentage ?? ""}
								onChange={(e) =>
									setEditData("LateFeePercentage", e.target.value)
								}
								onKeyDown={(event: KeyboardEvent<HTMLDivElement>) =>
									["e", "E", "+", "-"].includes(event.key) &&
									event.preventDefault()
								}
							/>
							{edited.includes("LateFeePercentage") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editSmall">
							<input
								type="number"
								value={data.LateFeeTerm ?? ""}
								onChange={(e) => setEditData("LateFeeTerm", e.target.value)}
								onKeyDown={(event: KeyboardEvent<HTMLDivElement>) =>
									["e", "E", "+", "-"].includes(event.key) &&
									event.preventDefault()
								}
							/>
							{edited.includes("LateFeeTerm") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editCheck">
							<Checkbox
								checked={data.LimitLiability ?? nullPlacment}
								onChange={(_e) =>
									setEditData(
										"LimitLiability",
										(!data.LimitLiability).toString(),
									)
								}
							/>
							{edited.includes("LimitLiability") ? (
								<span className="cellWarning material-icons">warning</span>
							) : null}
						</div>
					</td>
					<td>
						<div className="editPop">
							<TextPopUp
								text={data.LiabilityComments ?? nullPlacment}
								onChange={(e: string) => {
									setEditData("LiabilityComments", e)
								}}
								warning={edited.includes("LiabilityComments")}
								disabled={false}
								maxLength={5000}
							/>
						</div>
					</td>

					<td>
						{/*@ts-ignore*/}
						<input
							type="time"
							value={data.BusinessHoursStart}
							onChange={(e) => editBusinessHours(e, "BusinessHoursStart")}
							maxLength={5}
						></input>
					</td>

					<td>
						{/*@ts-ignore*/}
						<input
							type="time"
							value={data.BusinessHoursEnd}
							onChange={(e) => editBusinessHours(e, "BusinessHoursEnd")}
							maxLength={5}
						></input>
					</td>

					<td className="sticky-table-column-right">
						<span className="material-icons" onClick={() => setCancelCon(true)}>
							cancel
						</span>
						<span className="material-icons" onClick={() => setSaveCon(true)}>
							save
						</span>
					</td>
				</tr>
			) : (
				<tr key={index}>
					<td className="sticky-table-column-left">{data.ContractID}</td>
					<td onClick={() => setIsEditing(true)}>{data.CustomerID}</td>
					{/* Look up name from customerData by CustomerID so read and edit mode show the same value.
                            Falls back to raw BaseContractInfo name if CustomerID is null or not found. */}
					<td onClick={() => setIsEditing(true)}>
						{customerData?.find((x) => x.CustomerID === data.CustomerID)
							?.CustomerName ?? data.CustomerName}
					</td>
					<td onClick={() => setIsEditing(true)}>
						{data.Contract_Description}
					</td>
					<td onClick={() => setIsEditing(true)}>
						<div className="midNonEditPad">{data.VP_InCharge}</div>
					</td>
					<td onClick={() => setIsEditing(true)}>
						<div className="smallNonEditPad">{data.State}</div>
					</td>
					<td onClick={() => setIsEditing(true)}>{data.PaymentTerms}</td>
					<td onClick={() => setIsEditing(true)}>
						<div className="largeNonEditPad">{data.Discount}</div>
					</td>
					<td onClick={() => setIsEditing(true)}>
						<div className="dateNonEditPad">
							{data.StartDate?.split("T")[0]}
						</div>
					</td>
					<td onClick={() => setIsEditing(true)}>
						<div className="dateNonEditPad">{data.EndDate?.split("T")[0]}</div>
					</td>
					<td onClick={() => setIsEditing(true)}>
						{data.IsAutoRenew.toString()}
					</td>
					<td onClick={() => setIsEditing(true)}>
						<div className="midNonEditPad">{data.CPI?.toString()}</div>
					</td>
					<td onClick={() => setIsEditing(true)}>{data.CPI_Character}</td>
					<td onClick={() => setIsEditing(true)}>{data.CPIAmount}</td>
					<td onClick={() => setIsEditing(true)}>
						<div className="dateNonEditPad">
							{data.CPIEffectiveDate?.split("T")[0]}
						</div>
					</td>
					<td onClick={() => setIsEditing(true)}>
						<div className="midNonEditPad">
							{data.FuelSurcharge?.toString()}
						</div>
					</td>
					<td onClick={() => setIsEditing(true)}>{data.SurCharge_Character}</td>
					<td onClick={() => setIsEditing(true)}>{data.FuelSurchargeAmount}</td>
					<td onClick={() => setIsEditing(true)}>
						{data.FuelEffectiveDate?.split("T")[0]}
					</td>
					<td onClick={() => setIsEditing(true)}>
						<div className="dateNonEditPad">{data.FuelEndDate}</div>
					</td>
					<td onClick={() => setIsEditing(true)}>{data.PriceIncrease}</td>
					<td onClick={() => setIsEditing(true)}>
						<div className="largeNonEditPad">{data.Comments}</div>
					</td>
					<td onClick={() => setIsEditing(true)}>
						<div className="midNonEditPad">{data.IsActive.toString()}</div>
					</td>
					<td onClick={() => setIsEditing(true)}>
						{data.IsDistributed.toString()}
					</td>
					<td onClick={() => setIsEditing(true)}>
						<div className="dateNonEditPad">
							{data.DistributionDate?.split("T")[0]}
						</div>
					</td>
					<td onClick={() => setIsEditing(true)}>{data.IsTermableClause}</td>
					<td onClick={() => setIsEditing(true)}>{data.TermNotes}</td>
					<td onClick={() => setIsEditing(true)}>
						{data.IsLateFee?.toString()}
					</td>
					<td onClick={() => setIsEditing(true)}>{data.LateFeePercentage}</td>
					<td onClick={() => setIsEditing(true)}>{data.LateFeeTerm}</td>
					<td onClick={() => setIsEditing(true)}>
						{data.LimitLiability?.toString()}
					</td>
					<td onClick={() => setIsEditing(true)}>{data.LiabilityComments}</td>

					<td onClick={() => setIsEditing(true)}>
						{/*@ts-ignore*/}
						{data.BusinessHoursStart ? data.BusinessHoursStart.slice(0, 5) : ""}
					</td>
					<td onClick={() => setIsEditing(true)}>
						{/*@ts-ignore*/}
						{data.BusinessHoursEnd ? data.BusinessHoursEnd.slice(0, 5) : ""}
					</td>

					<th className="sticky-table-column-right">
						<span className="material-icons" onClick={() => setDeleteCon(true)}>
							delete
						</span>
					</th>
				</tr>
			)}
			{error ? <Redirect to="/css/contract-maintenance" /> : null}
			<Modal show={deleteCon} size="lg" onHide={() => setDeleteCon(false)}>
				<Modal.Header>
					<span>Are you sure you want to delete this record </span>
				</Modal.Header>
				<Modal.Footer>
					{/*r-18-lint-cn*/}
					{/*eslint-disable-next-line*/}
					<Button
						onClick={() => (deleteData(data.ContractID), setDeleteCon(false))}
						variant="primary"
					>
						Yes
					</Button>
					{/*r-18-lint-cn*/}
					{/*eslint-disable-next-line*/}
					<Button
						onClick={() => setDeleteCon(false)}
						variant="secondary"
					>
						No
					</Button>
				</Modal.Footer>
			</Modal>
			<Modal show={saveCon} size="lg" onHide={() => setSaveCon(false)}>
				<Modal.Header>
					<span>Are you sure you want to edit this record </span>
				</Modal.Header>
				<Modal.Footer>
					{/*r-18-lint-cn*/}
					{/*eslint-disable-next-line*/}
					<Button
						onClick={() => (uploadData(), setSaveCon(false))}
						variant="primary"
					>
						Yes
					</Button>
					<Button onClick={() => setSaveCon(false)} variant="secondary">
						No
					</Button>
				</Modal.Footer>
			</Modal>
			<Modal show={cancelCon} size="lg" onHide={() => setCancelCon(false)}>
				<Modal.Header>
					<span>Are you sure you want to cancel editing record </span>
				</Modal.Header>
				<Modal.Footer>
					{/*r-18-lint-cn*/}
					{/*eslint-disable-next-line*/}
					<Button
						onClick={() => (
							setIsEditing(false),
							setData({ ...props.data }),
							setEdited([]),
							setCancelCon(false)
						)}
						variant="primary"
					>
						Yes
					</Button>
					<Button onClick={() => setCancelCon(false)} variant="secondary">
						No
					</Button>
				</Modal.Footer>
			</Modal>
			<Prompt
				when={isEditing}
				message="You have unsaved changes, are you sure you want to leave?"
			/>
		</>
	)
}

export default ContractMaintenanceEditableCell
